import React, { useEffect, useState } from 'react';
import { Form, Input, Spin } from 'antd';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment/moment.js';
import Segmented from '../../../components/segmented/Segmented.jsx';
import Pagination from '../../../components/pagination/Pagination.jsx';
import TableWithSkeleton from '../../../components/table/TableWithSkeleton.jsx';
import ModalComponent from '../../../components/modals/ModalComponent.jsx';
import ConfirmationModal from '../../../components/modals/ConfirmationModal.jsx';
import LoadableButton from '../../../components/buttons/LoadableButton.jsx';
import columns from '../../../columns/ib/Kyc.js';
import Eye from '../../../assets/Eye.jsx';
import { KycManager, KycRequestAcceptRejectApi, GetAllKycByIdApi } from '../../../api/request/ib/kycManager.js';
import { showApiError } from '../../../utils/utils.js';

const { TextArea } = Input;

export default function KYCManagerMobile() {
  const [rejectForm] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sorter, setSorter] = useState({ SortBy: 'createdDate', SortOrder: '' });
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState(searchParams?.get('filter')?.trim() || 'Pending');
  const [selectedKycId, setSelectedKycId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [kycDetails, setKycDetails] = useState({});
  const [isKycDetailsLoading, setIsKycDetailsLoading] = useState(false);
  const [kycAccept, setKycAccept] = useState(false);
  const [kycRejection, setKycRejection] = useState(false);
  const [isReqLoading, setIsReqLoading] = useState(false);

  useEffect(() => {
    setSearchParams(`filter=${value}`);
  }, [value]);

  const getAllKyc = async () => {
    try {
      setIsLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        FilterText:
          value === 'Pending' ? 'KYCPending' : value === 'Approved' ? 'KYCApproved' : value === 'Declined' ? 'KYCDeclined' : value,
        SearchText: searchText,
        ...sorter,
      };
      const { data } = await KycManager(new URLSearchParams(params).toString());
      if (data?.success) {
        setData(
          (data?.data || []).map((item) => ({
            ...item,
            name: `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
            actionDate: item?.actionDate ? moment(item.actionDate).format('DD-MM-YYYY, hh:mm A') : '--',
            actionBy: item?.actionBy?.trim() === '' ? '--' : item?.actionBy,
            requestDate: item?.requestDate ? moment(item.requestDate).format('DD-MM-YYYY, hh:mm A') : '--',
            kycRequestDate: item?.kycRequestDate ? moment(item.kycRequestDate).format('DD-MM-YYYY, hh:mm A') : '--',
            kycApprovedDate: item?.kycApprovedDate ? moment(item.kycApprovedDate).format('DD-MM-YYYY, hh:mm A') : null,
            actions: (
              <button
                type="button"
                onClick={() => {
                  setSelectedUserId(item?.userId);
                  setSelectedKycId(item?.kycId || item?.id);
                  setIsOpenModal(true);
                }}
              >
                <Eye />
              </button>
            ),
          }))
        );
        setTotalRecord(data?.totalRecords || 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
    } catch (error) {
      setData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getKycById = async () => {
    try {
      setIsKycDetailsLoading(true);
      const { data } = await GetAllKycByIdApi(selectedUserId);
      setKycDetails(data?.data || {});
    } catch (error) {
    } finally {
      setIsKycDetailsLoading(false);
    }
  };

  useEffect(() => {
    getAllKyc();
  }, [page, pageSize, searchText, value, sorter]);

  useEffect(() => {
    if (selectedUserId && isOpenModal) getKycById();
  }, [selectedUserId, isOpenModal]);

  const approveKyc = async () => {
    try {
      setIsReqLoading(true);
      const { data } = await KycRequestAcceptRejectApi({
        approveReject: true,
        comment: 'Accepted.',
        kycId: [selectedKycId],
      });
      if (data?.success) {
        toast.success(data?.message);
        setKycAccept(false);
        setIsOpenModal(false);
        getAllKyc();
      }
    } catch (error) {
      showApiError(error);
    } finally {
      setIsReqLoading(false);
    }
  };

  const rejectKyc = async (values) => {
    try {
      setIsReqLoading(true);
      const { data } = await KycRequestAcceptRejectApi({
        approveReject: false,
        kycId: [selectedKycId],
        ...values,
      });
      if (data?.success) {
        toast.success(data?.message);
        setKycRejection(false);
        setIsOpenModal(false);
        rejectForm.resetFields();
        getAllKyc();
      }
    } catch (error) {
      showApiError(error);
    } finally {
      setIsReqLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, tableSorter) => {
    if (tableSorter?.order) {
      setSorter({ SortBy: tableSorter?.field, SortOrder: tableSorter?.order });
    } else {
      setSorter({ SortBy: 'createdDate', SortOrder: '' });
    }
  };

  return (
    <div className="space-y-4">
      <Segmented
        options={['Pending', 'Approved', 'Declined']}
        value={value}
        setValue={(next) => {
          setValue(next);
          setPage(1);
        }}
      />

      <Input
        size="large"
        placeholder="Search KYC"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onPressEnter={() => {
          setSearchText(search);
          setPage(1);
        }}
        className="rounded-xl"
      />

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <TableWithSkeleton columns={columns} data={data} loading={isLoading} onChange={handleTableChange} />
        </div>
      </div>

      <Pagination total={totalRecord} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />

      <ModalComponent isOpen={isOpenModal} setIsOpen={setIsOpenModal} title="KYC Details" width="100%">
        {isKycDetailsLoading ? (
          <div className="flex justify-center flex-col my-10">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Name', `${kycDetails?.firstName || ''} ${kycDetails?.lastName || ''}`.trim()],
                ['Email', kycDetails?.email],
                ['Phone', kycDetails?.phoneNo],
                ['Country', kycDetails?.countryName],
                ['Document Type', kycDetails?.kycDocumentType],
                ['Status', kycDetails?.kycStatus || value],
              ].map(([label, val]) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-sm font-semibold primary mt-1 break-all">{val || '--'}</p>
                </div>
              ))}
            </div>

            {(kycDetails?.kycStatus === 'Pending' || value === 'Pending') && (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 bg-green-600 text-white text-sm font-semibold py-2.5 rounded-lg"
                  onClick={() => {
                    setSelectedKycId(kycDetails?.kycId || selectedKycId);
                    setKycAccept(true);
                  }}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="flex-1 bg-red-600 text-white text-sm font-semibold py-2.5 rounded-lg"
                  onClick={() => {
                    setSelectedKycId(kycDetails?.kycId || selectedKycId);
                    setKycRejection(true);
                  }}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        )}
      </ModalComponent>

      <ConfirmationModal
        isOpen={kycAccept}
        setIsOpen={setKycAccept}
        type="success"
        message="Are you sure you want to approve this KYC request?"
        onConfirm={approveKyc}
        isLoading={isReqLoading}
        loadingLabel="Approving..."
      />

      <ModalComponent isOpen={kycRejection} setIsOpen={setKycRejection} title="Reject KYC" width="100%">
        <Form form={rejectForm} layout="vertical" onFinish={rejectKyc}>
          <Form.Item
            name="comment"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please enter reason.' }]}
          >
            <TextArea rows={4} placeholder="Enter rejection reason" />
          </Form.Item>
          <LoadableButton
            className="w-full bg-red-600 text-white font-semibold rounded-lg py-2"
            type="submit"
            lable="Reject"
            isLoading={isReqLoading}
            loadingLable="Rejecting..."
          />
        </Form>
      </ModalComponent>
    </div>
  );
}
